import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import adminApi from '../lib/adminApi';
import {
  Calendar,
  Clock,
  User,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Edit,
  Trash2,
  Filter,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  Search
} from 'lucide-react';
import { Button } from './ui/button';
import { formatDate } from '../lib/dateUtils';
import JoinedBadge from './leaves/JoinedBadge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

/**
 * LeaveManagementTable - Comprehensive leave management with approval, cancellation, and modification
 */
export default function LeaveManagementTable() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ pending: 0, approved: 0, rejected: 0, cancelled: 0 });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modals
  const [cancelModal, setCancelModal] = useState({ open: false, leave: null });
  const [modifyModal, setModifyModal] = useState({ open: false, leave: null });
  const [cancelReason, setCancelReason] = useState('');
  const [modifyData, setModifyData] = useState({ start_date: '', end_date: '', is_half_day: false, reason: '' });
  const [processing, setProcessing] = useState(false);

  // Fetch leaves
  const fetchLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (leaveTypeFilter) params.append('leave_type', leaveTypeFilter);
      
      const response = await adminApi.get(`/api/admin/leaves/all?${params.toString()}`);
      setLeaves(response.data.leaves || []);
      setSummary(response.data.summary || { pending: 0, approved: 0, rejected: 0, cancelled: 0 });
    } catch (error) {
      toast.error('Failed to fetch leaves');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, leaveTypeFilter]);

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  // Filter leaves by search
  const filteredLeaves = leaves.filter(leave => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      leave.employee_name?.toLowerCase().includes(query) ||
      leave.leave_type_name?.toLowerCase().includes(query) ||
      leave.reason?.toLowerCase().includes(query)
    );
  });

  // Approve leave
  const handleApprove = async (leaveId) => {
    setProcessing(true);
    try {
      await adminApi.put(`/api/admin/leaves/${leaveId}/approve?status=approved`);
      toast.success('Leave approved successfully');
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve leave');
    } finally {
      setProcessing(false);
    }
  };

  // Reject leave
  const handleReject = async (leaveId) => {
    setProcessing(true);
    try {
      await adminApi.put(`/api/admin/leaves/${leaveId}/approve?status=rejected&admin_note=Rejected by admin`);
      toast.success('Leave rejected');
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject leave');
    } finally {
      setProcessing(false);
    }
  };

  // Cancel leave
  const handleCancel = async () => {
    if (!cancelModal.leave) return;
    setProcessing(true);
    try {
      await adminApi.put(`/api/admin/leaves/${cancelModal.leave.id}/cancel`, { reason: cancelReason });
      toast.success('Leave cancelled successfully');
      setCancelModal({ open: false, leave: null });
      setCancelReason('');
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel leave');
    } finally {
      setProcessing(false);
    }
  };

  // Modify leave
  const handleModify = async () => {
    if (!modifyModal.leave) return;
    setProcessing(true);
    try {
      await adminApi.put(`/api/admin/leaves/${modifyModal.leave.id}/modify`, modifyData);
      toast.success('Leave modified successfully');
      setModifyModal({ open: false, leave: null });
      setModifyData({ start_date: '', end_date: '', is_half_day: false, reason: '' });
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to modify leave');
    } finally {
      setProcessing(false);
    }
  };

  // Open modify modal
  const openModifyModal = (leave) => {
    setModifyData({
      start_date: leave.start_date,
      end_date: leave.end_date,
      is_half_day: leave.is_half_day || false,
      reason: ''
    });
    setModifyModal({ open: true, leave });
  };

  // Status badge styles
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      approved: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      cancelled: 'bg-neutral-100 text-neutral-600 border-white/40'
    };
    return styles[status] || styles.pending;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return formatDate(dateStr);
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="soft-card overflow-hidden" data-testid="leave-management-table">
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/30 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-pink-100 rounded-xl">
              <Calendar className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1a1a2e]">All Leave Requests</h3>
              <p className="text-sm text-neutral-500">
                {summary.pending > 0 && (
                  <span className="text-amber-600 font-medium">{summary.pending} pending approval • </span>
                )}
                {leaves.length} total
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Search employee, type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 w-64 border border-white/40 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                data-testid="leave-search-input"
              />
            </div>
            
            {/* Filter Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-1"
            >
              <Filter className="w-4 h-4" />
              Filters
              {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </Button>
            
            {/* Refresh */}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchLeaves}
              disabled={loading}
              data-testid="refresh-leaves-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {/* Filters Row */}
        {showFilters && (
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-brutal-pink/30"
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
            
            <select
              value={leaveTypeFilter}
              onChange={(e) => setLeaveTypeFilter(e.target.value)}
              className="px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-brutal-pink/30"
              data-testid="type-filter"
            >
              <option value="">All Types</option>
              <option value="casual">Casual Leave</option>
              <option value="sick">Sick Leave</option>
              <option value="privilege">Privilege Leave</option>
              <option value="wfh">Work From Home</option>
              <option value="comp_off">Compensatory Off</option>
            </select>
            
            {(statusFilter || leaveTypeFilter) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setStatusFilter(''); setLeaveTypeFilter(''); }}
                className="text-neutral-500"
              >
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 p-4 bg-white/40 border-b">
        <div className="bg-white rounded-xl p-3 border border-amber-100">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" />
            <span className="text-sm text-neutral-600">Pending</span>
          </div>
          <p className="text-2xl font-bold text-amber-600 mt-1">{summary.pending}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-green-100">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm text-neutral-600">Approved</span>
          </div>
          <p className="text-2xl font-bold text-green-600 mt-1">{summary.approved}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-red-100">
          <div className="flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm text-neutral-600">Rejected</span>
          </div>
          <p className="text-2xl font-bold text-red-600 mt-1">{summary.rejected}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-white/40">
          <div className="flex items-center gap-2">
            <X className="w-4 h-4 text-neutral-500" />
            <span className="text-sm text-neutral-600">Cancelled</span>
          </div>
          <p className="text-2xl font-bold text-neutral-600 mt-1">{summary.cancelled}</p>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
          </div>
        ) : filteredLeaves.length === 0 ? (
          <div className="py-16 text-center text-neutral-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
            <p>No leave requests found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/40 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase">Leave Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase">Dates</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-600 uppercase">Days</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase">Applied On</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-600 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/30">
              {filteredLeaves.map((leave) => (
                <tr 
                  key={leave.id} 
                  className={`hover:bg-white/60 ${leave.status === 'pending' ? 'bg-amber-50/50' : ''}`}
                  data-testid={`leave-row-${leave.id}`}
                >
                  {/* Employee */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-pink-600" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1a1a2e] text-sm">{leave.employee_name}</p>
                        <p className="text-xs text-neutral-500">{leave.employee_email}</p>
                      </div>
                    </div>
                  </td>
                  
                  {/* Leave Type */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-neutral-700">{leave.leave_type_name}</span>
                    {leave.is_half_day && (
                      <span className="ml-1 text-xs text-neutral-500">(Half day)</span>
                    )}
                  </td>
                  
                  {/* Dates */}
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <span className="text-[#1a1a2e]">{formatDate(leave.start_date)}</span>
                      {leave.start_date !== leave.end_date && (
                        <>
                          <span className="text-neutral-400 mx-1">→</span>
                          <span className="text-[#1a1a2e]">{formatDate(leave.end_date)}</span>
                        </>
                      )}
                    </div>
                  </td>
                  
                  {/* Days */}
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-neutral-100 rounded-full text-sm font-medium text-neutral-700">
                      {leave.days_count}
                    </span>
                    <JoinedBadge leave={leave} />
                  </td>
                  
                  {/* Reason */}
                  <td className="px-4 py-3">
                    <p className="text-sm text-neutral-600 max-w-[200px] truncate" title={leave.reason}>
                      {leave.reason || '-'}
                    </p>
                  </td>
                  
                  {/* Applied On */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-neutral-500">{formatDate(leave.applied_on)}</span>
                  </td>
                  
                  {/* Status */}
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(leave.status)}`}>
                      {leave.status === 'pending' && <Clock className="w-3 h-3" />}
                      {leave.status === 'approved' && <CheckCircle className="w-3 h-3" />}
                      {leave.status === 'rejected' && <XCircle className="w-3 h-3" />}
                      {leave.status === 'cancelled' && <X className="w-3 h-3" />}
                      {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                    </span>
                  </td>
                  
                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      {leave.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(leave.id)}
                            disabled={processing}
                            className="p-1.5 hover:bg-green-50 rounded text-green-600"
                            title="Approve"
                            data-testid={`approve-leave-${leave.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleReject(leave.id)}
                            disabled={processing}
                            className="p-1.5 hover:bg-red-50 rounded text-red-600"
                            title="Reject"
                            data-testid={`reject-leave-${leave.id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setCancelModal({ open: true, leave })}
                            className="p-1.5 hover:bg-red-50 rounded text-red-600"
                            title="Cancel Leave"
                            data-testid={`cancel-leave-${leave.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {leave.status === 'approved' && (
                        <>
                          <button
                            onClick={() => openModifyModal(leave)}
                            className="p-1.5 hover:bg-blue-50 rounded text-blue-600"
                            title="Modify Dates"
                            data-testid={`modify-leave-${leave.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setCancelModal({ open: true, leave })}
                            className="p-1.5 hover:bg-red-50 rounded text-red-600"
                            title="Cancel Leave"
                            data-testid={`cancel-leave-${leave.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {(leave.status === 'rejected' || leave.status === 'cancelled') && (
                        <span className="text-xs text-neutral-400">-</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Cancel Modal */}
      <Dialog open={cancelModal.open} onOpenChange={(open) => !open && setCancelModal({ open: false, leave: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Leave</DialogTitle>
            <DialogDescription>
              {cancelModal.leave?.status === 'approved'
                ? 'Are you sure you want to cancel this approved leave? The leave balance will be restored.'
                : 'Are you sure you want to cancel this pending leave request? It will be marked cancelled instead of approved or rejected.'}
            </DialogDescription>
          </DialogHeader>
          
          {cancelModal.leave && (
            <div className="py-4 space-y-3">
              <div className="bg-white/40 rounded-xl p-3">
                <p className="text-sm"><strong>Employee:</strong> {cancelModal.leave.employee_name}</p>
                <p className="text-sm"><strong>Type:</strong> {cancelModal.leave.leave_type_name}</p>
                <p className="text-sm"><strong>Dates:</strong> {cancelModal.leave.start_date} to {cancelModal.leave.end_date}</p>
                <p className="text-sm"><strong>Days:</strong> {cancelModal.leave.days_count}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Cancellation Reason (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter reason for cancellation..."
                  className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-brutal-pink/30"
                  rows={3}
                  data-testid="cancel-reason-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModal({ open: false, leave: null })}>
              Keep Leave
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancel}
              disabled={processing}
              data-testid="confirm-cancel-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Cancel Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modify Modal */}
      <Dialog open={modifyModal.open} onOpenChange={(open) => !open && setModifyModal({ open: false, leave: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modify Leave Dates</DialogTitle>
            <DialogDescription>
              Update the leave dates. Balance will be adjusted automatically.
            </DialogDescription>
          </DialogHeader>
          
          {modifyModal.leave && (
            <div className="py-4 space-y-4">
              <div className="bg-white/40 rounded-xl p-3">
                <p className="text-sm"><strong>Employee:</strong> {modifyModal.leave.employee_name}</p>
                <p className="text-sm"><strong>Type:</strong> {modifyModal.leave.leave_type_name}</p>
                <p className="text-sm"><strong>Current Days:</strong> {modifyModal.leave.days_count}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={modifyData.start_date}
                    onChange={(e) => setModifyData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-brutal-pink/30"
                    data-testid="modify-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={modifyData.end_date}
                    onChange={(e) => setModifyData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-brutal-pink/30"
                    data-testid="modify-end-date"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="half_day"
                  checked={modifyData.is_half_day}
                  onChange={(e) => setModifyData(prev => ({ ...prev, is_half_day: e.target.checked }))}
                  className="rounded border-white/40 text-pink-600 focus:ring-pink-500"
                />
                <label htmlFor="half_day" className="text-sm text-neutral-700">Half day leave</label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Reason for Modification</label>
                <textarea
                  value={modifyData.reason}
                  onChange={(e) => setModifyData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Enter reason..."
                  className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-brutal-pink/30"
                  rows={2}
                  data-testid="modify-reason-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModifyModal({ open: false, leave: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleModify}
              disabled={processing || !modifyData.start_date || !modifyData.end_date}
              data-testid="confirm-modify-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
